import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, CreditCard, Banknote, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { ordersApi } from '../services/api';
import { formatCurrency, formatApiError, Spinner } from '../utils/helpers';
import { useLanguage } from '../contexts/LanguageContext';

const METHODS = [
  { id: 'cash', icon: Banknote, label: { id: 'Tunai', en: 'Cash' } },
  { id: 'transfer', icon: CreditCard, label: { id: 'Transfer', en: 'Transfer' } },
  { id: 'qris', icon: Smartphone, label: { id: 'QRIS', en: 'QRIS' } },
];

export default function PaymentModal({ order, onClose, onSuccess }) {
  const { lang, t } = useLanguage();
  const remaining = order.remaining_amount || 0;

  // Display in Indonesian thousand format: 33000 → "33.000"
  const fmt = (n) => {
    const num = parseInt(String(n).replace(/\D/g, ''), 10);
    return isNaN(num) || num === 0 ? '' : num.toLocaleString('id-ID');
  };
  const parse = (s) => parseInt(String(s).replace(/\./g, '').replace(/,/g, ''), 10) || 0;

  const [amountDisplay, setAmountDisplay] = useState(fmt(remaining));
  const [method, setMethod] = useState('cash');
  const [notes, setNotes] = useState('');

  const amountNum = parse(amountDisplay);

  const handleAmountChange = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, '');
    const num = parseInt(digits, 10) || 0;
    setAmountDisplay(num ? num.toLocaleString('id-ID') : '');
  };

  const mutation = useMutation({
    mutationFn: () => ordersApi.addPayment(order.id, {
      amount: amountNum,
      payment_method: method,
      payment_type: amountNum >= remaining ? 'full' : 'partial',
      notes,
    }),
    onSuccess: () => {
      toast.success(t('Pembayaran berhasil dicatat!', 'Payment recorded!'));
      onSuccess?.();
    },
    onError: err => toast.error(formatApiError(err)),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!amountNum || amountNum <= 0) { toast.error(t('Masukkan jumlah yang valid', 'Enter valid amount')); return; }
    if (amountNum > remaining) { toast.error(t('Jumlah melebihi sisa tagihan', 'Amount exceeds remaining balance')); return; }
    mutation.mutate();
  };

  const setFull = () => setAmountDisplay(fmt(remaining));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-800 font-manrope">{t('Catat Pembayaran', 'Record Payment')}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{order.tracking_code} · {order.customer_name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-slate-100 transition-colors">
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3 text-center">
            {[
              { label: 'Total', value: order.total_amount, color: 'text-slate-800' },
              { label: t('Terbayar', 'Paid'), value: order.paid_amount, color: 'text-green-600' },
              { label: t('Sisa', 'Remaining'), value: order.remaining_amount, color: 'text-orange-600' },
            ].map(item => (
              <div key={item.label} className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-400 mb-1">{item.label}</p>
                <p className={`text-sm font-bold ${item.color} font-manrope`}>{formatCurrency(item.value)}</p>
              </div>
            ))}
          </div>

          {/* Amount */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-slate-700">{t('Jumlah Bayar', 'Payment Amount')} *</label>
              <button type="button" onClick={setFull}
                className="text-xs text-[#0f6c44] font-semibold hover:underline">
                {t('Bayar Lunas', 'Pay Full')} ({formatCurrency(remaining)})
              </button>
            </div>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium select-none">Rp</span>
              <input
                type="text" inputMode="numeric" value={amountDisplay}
                onChange={handleAmountChange} required autoFocus
                placeholder="0"
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] text-slate-800 font-semibold tracking-wide"
              />
            </div>
            {amountNum > 0 && amountNum > remaining && (
              <p className="text-xs text-red-500 mt-1">{t('Jumlah melebihi sisa tagihan', 'Amount exceeds remaining balance')}</p>
            )}
          </div>

          {/* Method */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('Metode Pembayaran', 'Payment Method')}</label>
            <div className="grid grid-cols-3 gap-2">
              {METHODS.map(m => (
                <button
                  key={m.id} type="button" onClick={() => setMethod(m.id)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${
                    method === m.id ? 'border-[#0f6c44] bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <m.icon size={20} className={method === m.id ? 'text-[#0f6c44]' : 'text-slate-400'} />
                  <span className={`text-xs font-medium ${method === m.id ? 'text-[#0f6c44]' : 'text-slate-500'}`}>
                    {m.label[lang]}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('Catatan', 'Notes')} ({t('opsional', 'optional')})</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder={t('Catatan pembayaran...', 'Payment notes...')}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 h-11 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors">
              {t('Batal', 'Cancel')}
            </button>
            <button type="submit" disabled={mutation.isPending || !amountNum}
              className="flex-1 h-11 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] transition-all flex items-center justify-center gap-2 disabled:opacity-70">
              {mutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : t('Simpan', 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
